import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useReducer } from 'react';
import PropertyInputMultipleEmailPopoverBody from './email/components/PropertyInputMultipleEmailPopoverBody';
import UIPopover from 'UIComponents/tooltip/UIPopover';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import I18n from 'I18n';
import UITruncateString from 'UIComponents/text/UITruncateString';
import uniqueId from 'transmute/uniqueId';
import MultipleEmailInputErrorBanner from './email/components/MultipleEmailInputErrorBanner';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import promiseDone from 'hs-promise-utils/promiseDone';

const generateId = () => {
  return uniqueId('email');
};

const buildInitialState = ({
  additionalEmails,
  value
}) => {
  const additionalEmailsArray = additionalEmails !== undefined && additionalEmails !== null && additionalEmails !== '' ? additionalEmails.split(';') : [];
  return {
    draftAdditionalEmails: additionalEmailsArray.map(email => ({
      id: generateId(),
      isNewEmail: false,
      value: email
    })),
    draftDeletedEmail: undefined,
    draftPrimaryEmail: {
      id: generateId(),
      isNewEmail: !value,
      value: value || ''
    },
    isEditable: true,
    shouldShowErrorBanner: false
  };
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADDITIONAL_EMAIL_INPUT_ADDED':
      {
        return Object.assign({}, state, {
          draftAdditionalEmails: [...state.draftAdditionalEmails, {
            id: generateId(),
            isNewEmail: true,
            value: ''
          }]
        });
      }

    case 'ADDITIONAL_EMAIL_DELETE_SUCCEEDED':
      {
        return Object.assign({}, state, {
          draftAdditionalEmails: state.draftAdditionalEmails.filter(email => email.id !== action.payload.email.id),
          draftDeletedEmail: undefined,
          isEditable: true,
          shouldShowErrorBanner: false
        });
      }

    case 'ASYNC_ACTION_STARTED':
      {
        return Object.assign({}, state, {
          isEditable: false,
          shouldShowErrorBanner: false
        });
      }

    case 'ASYNC_ACTION_FAILED':
      {
        return Object.assign({}, state, {
          isEditable: true,
          shouldShowErrorBanner: true
        });
      }

    case 'ADDITIONAL_EMAIL_SAVE_SUCCEEDED':
      {
        return Object.assign({}, state, {
          draftAdditionalEmails: state.draftAdditionalEmails.reduce((acc, email) => {
            if (email.id === action.payload.email.id) {
              acc.push(Object.assign({}, action.payload.email, {
                isNewEmail: false
              }));
            } else {
              acc.push(email);
            }

            return acc;
          }, []),
          isEditable: true
        });
      }

    case 'EMAIL_PROMOTED_TO_PRIMARY_SUCCEEDED':
      {
        if (!state.draftPrimaryEmail || state.draftPrimaryEmail.isNewEmail) {
          return Object.assign({}, state, {
            draftPrimaryEmail: action.payload.email,
            draftAdditionalEmails: state.draftAdditionalEmails.filter(email => email.id !== action.payload.email.id),
            isEditable: true
          });
        }

        return Object.assign({}, state, {
          draftPrimaryEmail: action.payload.email,
          draftAdditionalEmails: [// The type cast here is because it is impossible to promote a
          // additional email when a primary email is not set so this will always
          // be defined
          state.draftPrimaryEmail, ...state.draftAdditionalEmails.filter(email => email.id !== action.payload.email.id)],
          isEditable: true
        });
      }

    case 'PRIMARY_EMAIL_INPUT_ADDED':
      {
        return Object.assign({}, state, {
          draftPrimaryEmail: {
            id: generateId(),
            isNewEmail: true,
            value: ''
          }
        });
      }

    case 'PRIMARY_EMAIL_DELETE_SUCCEEDED':
      {
        return Object.assign({}, state, {
          draftDeletedEmail: undefined,
          draftPrimaryEmail: undefined,
          isEditable: true
        });
      }

    case 'PRIMARY_EMAIL_SAVE_SUCCEEDED':
      {
        return Object.assign({}, state, {
          draftPrimaryEmail: Object.assign({}, action.payload.email, {
            isNewEmail: false
          }),
          isEditable: true
        });
      }

    case 'ERROR_BANNER_CLOSED':
      {
        return Object.assign({}, state, {
          shouldShowErrorBanner: false
        });
      }

    case 'EMAIL_DELETE_STARTED':
      {
        return Object.assign({}, state, {
          draftDeletedEmail: action.payload.email
        });
      }

    case 'EMAIL_DELETE_CANCELED':
      {
        return Object.assign({}, state, {
          draftDeletedEmail: undefined
        });
      }

    case 'EMAIL_DELETE_FAILED':
      {
        return Object.assign({}, state, {
          isEditable: true,
          shouldShowErrorBanner: true,
          draftDeletedEmail: undefined
        });
      }

    default:
      {
        throw new Error('Unknown action detected');
      }
  }
};
/**
 * Default action handler used if the parent does not provide
 * a callback function for an action. Action handlers are expected
 * to return a Promise indicating their success/failure.
 *
 * The default handler always fails, because the multiple email
 * input doesn't work unless the host app handles each callback
 * and persists changes to the BE.
 *
 * (Email changes are not simple property changes. They require
 * BE involvement to update contact identities. The `email` and
 * `hs_additional_email` properties are updated as a side-effect
 * of updating contact identities.)
 */


const emptyAction = () => Promise.reject(new Error('Not implemented'));

const PropertyInputMultipleEmailV2 = ({
  actions = {
    addAdditionalEmail: emptyAction,
    deleteAdditionalEmail: emptyAction,
    makeEmailPrimary: emptyAction,
    updatePrimaryEmail: emptyAction,
    updateAdditionalEmail: emptyAction
  },
  accessLevel,
  additionalEmails,
  'data-selenium-test': dataSeleniumTest,
  className,
  disabled,
  objectTypeId,
  popoverOpen,
  property,
  readOnly,
  setPopoverOpen,
  subjectId,
  value,
  showAutoSaveText = true
}) => {
  // Unlike most other inputs this input is not controlled by the value prop.
  // It emits actions instead of using the regular onChange flow to update
  // values and the consuming app is expected to implement handlers for those
  // actions.
  const [state, dispatch] = useReducer(reducer, {
    additionalEmails,
    value
  }, buildInitialState);
  const handleLabelClicked = useCallback(() => {
    // We don't want to allow opening the popover if the input is disabled or
    // read only
    if (readOnly || disabled) {
      return;
    }

    setPopoverOpen(true);
  }, [disabled, readOnly, setPopoverOpen]);
  const handleOpenChanged = useCallback(evt => {
    setPopoverOpen(evt.target.value);
  }, [setPopoverOpen]);
  const handleErrorClosed = useCallback(() => dispatch({
    type: 'ERROR_BANNER_CLOSED'
  }), []);
  const handleAdditionalEmailInputAdded = useCallback(() => {
    dispatch({
      type: 'ADDITIONAL_EMAIL_INPUT_ADDED'
    });
  }, []);
  const handleAdditionalEmailDeleted = useCallback(email => {
    dispatch({
      type: 'ASYNC_ACTION_STARTED'
    });
    promiseDone(actions.deleteAdditionalEmail(email.value).then(() => dispatch({
      type: 'ADDITIONAL_EMAIL_DELETE_SUCCEEDED',
      payload: {
        email
      }
    })).catch(() => // We have a special action for failed email deletes because we also
    // have to clear draftDeletedEmail. It does basically the same thing
    // as `ASYNC_ACTION_FAILED` but with that extra step
    dispatch({
      type: 'EMAIL_DELETE_FAILED'
    })));
  }, [actions]);
  const handleAdditionalEmailSaved = useCallback((email, oldEmail) => {
    dispatch({
      type: 'ASYNC_ACTION_STARTED'
    });

    if (email.isNewEmail) {
      promiseDone(actions.addAdditionalEmail(email.value).then(() => dispatch({
        type: 'ADDITIONAL_EMAIL_SAVE_SUCCEEDED',
        payload: {
          email
        }
      })).catch(() => dispatch({
        type: 'ASYNC_ACTION_FAILED'
      })));
    } else {
      promiseDone(actions.updateAdditionalEmail(oldEmail.value, email.value).then(() => dispatch({
        type: 'ADDITIONAL_EMAIL_SAVE_SUCCEEDED',
        payload: {
          email
        }
      })).catch(() => dispatch({
        type: 'ASYNC_ACTION_FAILED'
      })));
    }
  }, [actions]);
  const handlePromoteToPrimary = useCallback(email => {
    dispatch({
      type: 'ASYNC_ACTION_STARTED'
    });
    promiseDone(actions.makeEmailPrimary(email.value).then(() => dispatch({
      type: 'EMAIL_PROMOTED_TO_PRIMARY_SUCCEEDED',
      payload: {
        email
      }
    })).catch(() => dispatch({
      type: 'ASYNC_ACTION_FAILED'
    })));
  }, [actions]);
  const handlePrimaryEmailInputAdded = useCallback(() => {
    dispatch({
      type: 'PRIMARY_EMAIL_INPUT_ADDED'
    });
  }, []);
  const handlePrimaryEmailDeleted = useCallback(email => {
    dispatch({
      type: 'ASYNC_ACTION_STARTED'
    });
    promiseDone(actions.updatePrimaryEmail('').then(() => {
      dispatch({
        type: 'PRIMARY_EMAIL_DELETE_SUCCEEDED',
        payload: {
          email
        }
      });
    }).catch(() => // We have a special action for failed email deletes because we also
    // have to clear draftDeletedEmail. It does basically the same thing
    // as `ASYNC_ACTION_FAILED` but with that extra step
    dispatch({
      type: 'EMAIL_DELETE_FAILED'
    })));
  }, [actions]);
  const handlePrimaryEmailSaved = useCallback(email => {
    dispatch({
      type: 'ASYNC_ACTION_STARTED'
    });
    promiseDone(actions.updatePrimaryEmail(email.value).then(() => dispatch({
      type: 'PRIMARY_EMAIL_SAVE_SUCCEEDED',
      payload: {
        email
      }
    })).catch(() => dispatch({
      type: 'ASYNC_ACTION_FAILED'
    })));
  }, [actions]);
  const handleDeletePrimaryEmailStarted = useCallback(email => {
    // In this case the input was just added so we just want to remove it.
    // We don't have to trigger an action or anything because the email has
    // not been saved yet so we just have to remove it from state.
    if (email.isNewEmail) {
      dispatch({
        type: 'PRIMARY_EMAIL_DELETE_SUCCEEDED',
        payload: {
          email
        }
      });
    } else {
      dispatch({
        type: 'EMAIL_DELETE_STARTED',
        payload: {
          email
        }
      });
    }
  }, []);
  const handleDeleteAdditionalEmailStarted = useCallback(email => {
    // In this case the input was just added so we just want to remove it.
    // We don't have to trigger an action or anything because the email has
    // not been saved yet so we just have to remove it from state.
    if (email.isNewEmail) {
      dispatch({
        type: 'ADDITIONAL_EMAIL_DELETE_SUCCEEDED',
        payload: {
          email
        }
      });
    } else {
      dispatch({
        type: 'EMAIL_DELETE_STARTED',
        payload: {
          email
        }
      });
    }
  }, []);
  const handleDeleteEmailConfirmed = useCallback(() => {
    if (!state.draftDeletedEmail) {
      return;
    } // This handler is called with both primary emails and additional emails.
    // Since we don't know which one it is we check the ids to figure out if
    // it's the primary email.


    if (state.draftPrimaryEmail && state.draftDeletedEmail.id === state.draftPrimaryEmail.id) {
      handlePrimaryEmailDeleted(state.draftDeletedEmail);
    } else {
      handleAdditionalEmailDeleted(state.draftDeletedEmail);
    }
  }, [handleAdditionalEmailDeleted, handlePrimaryEmailDeleted, state.draftDeletedEmail, state.draftPrimaryEmail]);
  const handleDeleteEmailCanceled = useCallback(() => dispatch({
    type: 'EMAIL_DELETE_CANCELED'
  }), []);
  const allEmailsArray = useMemo(() => {
    const primaryEmail = state.draftPrimaryEmail ? state.draftPrimaryEmail.value : '';
    return [primaryEmail, ...state.draftAdditionalEmails.map(email => email.value)];
  }, [state.draftAdditionalEmails, state.draftPrimaryEmail]);

  const popoverContent = /*#__PURE__*/_jsxs(_Fragment, {
    children: [state.shouldShowErrorBanner && /*#__PURE__*/_jsx(MultipleEmailInputErrorBanner, {
      onClose: handleErrorClosed
    }), /*#__PURE__*/_jsx(PropertyInputMultipleEmailPopoverBody, {
      accessLevel: accessLevel,
      additionalEmails: state.draftAdditionalEmails,
      disabled: !state.isEditable,
      primaryEmail: state.draftPrimaryEmail,
      objectTypeId: objectTypeId,
      subjectId: subjectId,
      onPrimaryEmailInputAdded: handlePrimaryEmailInputAdded,
      onPrimaryEmailDeleted: handleDeletePrimaryEmailStarted,
      onPrimaryEmailSaved: handlePrimaryEmailSaved,
      onPromoteToPrimary: handlePromoteToPrimary,
      onAdditionalEmailInputAdded: handleAdditionalEmailInputAdded,
      onAdditionalEmailDeleted: handleDeleteAdditionalEmailStarted,
      onAdditionalEmailSaved: handleAdditionalEmailSaved,
      property: property,
      showAutoSaveText: showAutoSaveText
    })]
  });

  return /*#__PURE__*/_jsxs(UIGridItem, {
    size: {
      xs: 12
    },
    className: "p-x-0",
    children: [/*#__PURE__*/_jsx(UIPopover, {
      open: popoverOpen,
      closeOnOutsideClick: true,
      content: popoverContent,
      onOpenChange: handleOpenChanged,
      width: 500,
      className: "p-bottom-4",
      placement: "right",
      autoPlacement: "horiz",
      children: /*#__PURE__*/_jsx("div", {
        onClick: handleLabelClicked,
        className: className,
        "data-selenium-test": dataSeleniumTest,
        children: /*#__PURE__*/_jsx(UITruncateString, {
          children: allEmailsArray.join(I18n.text('customerDataProperties.PropertyInputMultipleEmailV2.commaSeparatorKey'))
        })
      })
    }), state.draftDeletedEmail && /*#__PURE__*/_jsx(UIConfirmModal, {
      confirmLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.confirmLabel"
      }),
      confirmUse: "danger",
      description: /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.description" // draftDeletedEmail must exist in order for the modal to render
          // so we can safely type cast it here
          ,
          options: {
            email: state.draftDeletedEmail.value
          }
        })
      }),
      message: /*#__PURE__*/_jsx("span", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.title"
        })
      }),
      onConfirm: handleDeleteEmailConfirmed,
      onReject: handleDeleteEmailCanceled,
      rejectLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataProperties.PropertyInputMultipleEmailV2.delete.rejectLabel"
      }),
      size: "auto",
      use: "danger"
    })]
  });
};

export default /*#__PURE__*/memo(PropertyInputMultipleEmailV2);